import { useEffect, useState } from "react";
import { Users, Plus, X, Send, CheckCircle2, Settings2 } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  listCollaborators, addCollaborator, removeCollaborator, sendCollaboratorInvites, setCollaboratorSections,
} from "../lib/api";

/**
 * Contributors (phase 1 — data model only).
 *
 * Lets the evaluation owner add / remove contributor email addresses on the
 * SAME assessment. Adding a contributor here does NOT yet grant that person
 * resume/access — this only establishes the collaborator relationship in the
 * assessment data model. Owner email behaviour, resume, scoring, etc. are
 * untouched.
 */
export const ContributorsSection = ({ assessmentId, ownerEmail, currentEmail, sections = [], groups = [] }) => {
  const isOwner = !!(currentEmail && ownerEmail &&
    currentEmail.trim().toLowerCase() === ownerEmail.trim().toLowerCase());
  const [collaborators, setCollaborators] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteMsg, setInviteMsg] = useState("");
  // Section-assignment editing (responsibility, not access).
  const [editingEmail, setEditingEmail] = useState(null);
  const [draftSections, setDraftSections] = useState([]);
  const [draftGroups, setDraftGroups] = useState([]);
  const [savingAssign, setSavingAssign] = useState(false);

  useEffect(() => {
    // Owner-only: contributors never fetch the management list.
    if (!assessmentId || !isOwner) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await listCollaborators(assessmentId, currentEmail);
        if (!cancelled) setCollaborators(data?.collaborators || []);
      } catch {
        /* ignore — treat as empty */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [assessmentId, isOwner, currentEmail]);

  const handleAdd = async () => {
    setError("");
    const value = email.trim().toLowerCase();
    if (!value) {
      setError("Please enter a contributor email.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError("Enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      const data = await addCollaborator(assessmentId, value, currentEmail);
      setCollaborators(data?.collaborators || []);
      setEmail("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't add contributor. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (target) => {
    setError("");
    setBusy(true);
    try {
      const data = await removeCollaborator(assessmentId, target, currentEmail);
      setCollaborators(data?.collaborators || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't remove contributor. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleKey = (e) => { if (e.key === "Enter") handleAdd(); };

  const activeCount = collaborators.filter(
    (c) => (c.status || "active") === "active"
  ).length;

  const handleInvite = async () => {
    setInviteMsg("");
    setError("");
    setInviting(true);
    try {
      const res = await sendCollaboratorInvites(assessmentId, currentEmail);
      setCollaborators(res?.collaborators || []);
      if (res?.email_configured === false) {
        setInviteMsg("");
        setError("Email delivery is not configured. Invitations were not sent.");
        return;
      }
      const total = res?.total ?? 0;
      const sent = res?.sent ?? 0;
      const failed = res?.failed || [];
      let msg = `Invitations sent to ${sent} of ${total} contributor${total === 1 ? "" : "s"}.`;
      if (failed.length) msg += ` Failed: ${failed.join(", ")}.`;
      setInviteMsg(msg);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't send invitations. Please try again.");
    } finally {
      setInviting(false);
    }
  };

  // Owner-only management UI: contributors never see the section at all.
  if (!assessmentId || !isOwner) return null;

  const labelFor = (id) => (sections.find((s) => s.id === id)?.title) || id;
  const groupNameFor = (id) => (groups.find((g) => g.id === id)?.name);
  const ALL = "*";
  // Human-readable summary that ignores raw UUIDs and stale (deleted) groups.
  const groupSummary = (ids) => {
    if (!ids || ids.length === 0) return "";
    if (ids.includes(ALL)) return "All Warehouse Groups";
    const names = ids.map(groupNameFor).filter(Boolean);
    return names.join(", ");
  };
  const processSummary = (ids) => {
    if (!ids || ids.length === 0) return "";
    if (ids.includes(ALL)) return "All Processes";
    return ids.map(labelFor).join(", ");
  };
  const openAssign = (c) => {
    setEditingEmail(c.email);
    setDraftSections(c.assigned_sections || []);
    setDraftGroups(c.assigned_groups || []);
    setError("");
  };
  const toggleDraft = (id) =>
    setDraftSections((p) => {
      const base = p.filter((x) => x !== ALL);
      return base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    });
  const toggleAllProcesses = () =>
    setDraftSections((p) => (p.includes(ALL) ? [] : [ALL]));
  const toggleDraftGroup = (id) =>
    setDraftGroups((p) => {
      const base = p.filter((x) => x !== ALL);
      return base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    });
  const toggleAllGroups = () =>
    setDraftGroups((p) => (p.includes(ALL) ? [] : [ALL]));
  const saveAssign = async (emailAddr) => {
    setSavingAssign(true);
    setError("");
    try {
      const data = await setCollaboratorSections(assessmentId, emailAddr, draftSections, currentEmail, draftGroups);
      setCollaborators(data?.collaborators || []);
      setEditingEmail(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't save assignment. Please try again.");
    } finally {
      setSavingAssign(false);
    }
  };

  return (
    <div
      data-testid="contributors-section"
      className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-4 sm:p-5 mb-4"
    >
      <div className="flex items-center gap-2 mb-1">
        <Users className="w-4 h-4 text-[#025979]" strokeWidth={1.5} />
        <h2
          className="text-sm font-semibold text-[#1F2937] uppercase tracking-wider"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          Contributors
        </h2>
      </div>
      <p className="text-[11px] text-[#6B7280] mb-4 leading-relaxed">
        Add colleagues who are helping complete this evaluation. They&apos;ll be linked to this assessment.
      </p>

      {/* Current contributors */}
      <div className="space-y-2 mb-4" data-testid="contributors-list">
        {loading ? (
          <p className="text-xs text-[#9CA3AF]">Loading contributors…</p>
        ) : collaborators.length === 0 ? (
          <p data-testid="contributors-empty" className="text-xs text-[#9CA3AF]">
            No contributors added yet.
          </p>
        ) : (
          collaborators.map((c) => (
            <div
              key={c.email}
              data-testid={`contributor-row-${c.email}`}
              className="border border-[#E5E7EB] rounded-sm px-3 py-2 bg-[#FAFBFC]"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex flex-col min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-[#111827] truncate">{c.email}</span>
                    {c.invite_sent_at && (
                      <span
                        data-testid={`contributor-invited-${c.email}`}
                        className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-[#ECFDF5] text-[#065F46] border border-[#065F46]/15"
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        Invited
                      </span>
                    )}
                  </div>
                  {(groupSummary(c.assigned_groups) || processSummary(c.assigned_sections)) && (
                    <div data-testid={`contributor-assigned-${c.email}`} className="text-[11px] text-[#6B7280] mt-0.5 space-y-0.5">
                      {groupSummary(c.assigned_groups) && (
                        <p className="truncate"><span className="font-medium">Groups:</span> {groupSummary(c.assigned_groups)}</p>
                      )}
                      {processSummary(c.assigned_sections) && (
                        <p className="truncate"><span className="font-medium">Processes:</span> {processSummary(c.assigned_sections)}</p>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button
                    type="button"
                    data-testid={`contributor-assign-${c.email}`}
                    onClick={() => (editingEmail === c.email ? setEditingEmail(null) : openAssign(c))}
                    disabled={busy || (sections.length === 0 && groups.length === 0)}
                    className="inline-flex items-center gap-1 text-xs text-[#025979] hover:text-[#03738C] transition-colors disabled:opacity-40"
                  >
                    <Settings2 className="w-3.5 h-3.5" />
                    Edit Assignment
                  </button>
                  <button
                    type="button"
                    data-testid={`contributor-remove-${c.email}`}
                    onClick={() => handleRemove(c.email)}
                    disabled={busy}
                    className="inline-flex items-center gap-1 text-xs text-[#B91C1C] hover:text-[#7F1D1D] transition-colors disabled:opacity-50"
                  >
                    <X className="w-3.5 h-3.5" />
                    Remove
                  </button>
                </div>
              </div>
              {editingEmail === c.email && (
                <div data-testid={`assign-panel-${c.email}`} className="mt-2 pt-2 border-t border-[#E5E7EB]">
                  <p className="text-[11px] text-[#6B7280] mb-2">
                    Indicate responsibility only — contributors can still view and answer every group and process.
                  </p>

                  {/* Assigned Warehouse Groups */}
                  <p className="text-[11px] font-semibold text-[#374151] mb-1">Assigned Warehouse Groups</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-3">
                    <label
                      data-testid={`assign-group-all-${c.email}`}
                      className="flex items-center gap-2 text-xs text-[#374151] cursor-pointer font-medium"
                    >
                      <input type="checkbox" checked={draftGroups.includes(ALL)} onChange={toggleAllGroups} className="accent-[#025979]" />
                      All Warehouse Groups
                    </label>
                    {groups.map((g) => (
                      <label
                        key={g.id}
                        data-testid={`assign-group-${c.email}-${g.id}`}
                        className="flex items-center gap-2 text-xs text-[#374151] cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={draftGroups.includes(ALL) || draftGroups.includes(g.id)}
                          disabled={draftGroups.includes(ALL)}
                          onChange={() => toggleDraftGroup(g.id)}
                          className="accent-[#025979]"
                        />
                        {g.name}
                      </label>
                    ))}
                  </div>

                  {/* Assigned Processes */}
                  <p className="text-[11px] font-semibold text-[#374151] mb-1">Assigned Processes <span className="font-normal text-[#9CA3AF]">(apply to all selected groups)</span></p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-2">
                    <label
                      data-testid={`assign-process-all-${c.email}`}
                      className="flex items-center gap-2 text-xs text-[#374151] cursor-pointer font-medium"
                    >
                      <input type="checkbox" checked={draftSections.includes(ALL)} onChange={toggleAllProcesses} className="accent-[#025979]" />
                      All Processes
                    </label>
                    {sections.map((s) => (
                      <label
                        key={s.id}
                        data-testid={`assign-option-${c.email}-${s.id}`}
                        className="flex items-center gap-2 text-xs text-[#374151] cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={draftSections.includes(ALL) || draftSections.includes(s.id)}
                          disabled={draftSections.includes(ALL)}
                          onChange={() => toggleDraft(s.id)}
                          className="accent-[#025979]"
                        />
                        {s.title}
                      </label>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid={`assign-save-${c.email}`}
                      onClick={() => saveAssign(c.email)}
                      disabled={savingAssign}
                      className="bg-[#025979] hover:bg-[#03738C] text-white text-xs rounded-sm h-7 px-3 disabled:opacity-50"
                    >
                      {savingAssign ? "Saving…" : "Save"}
                    </Button>
                    <Button
                      data-testid={`assign-cancel-${c.email}`}
                      variant="outline"
                      onClick={() => setEditingEmail(null)}
                      className="text-xs rounded-sm h-7 px-3"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add contributor */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          data-testid="contributor-email-input"
          type="email"
          placeholder="Add contributor email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={handleKey}
          disabled={busy}
          className="rounded-sm text-sm border-[#D1D5DB] focus:border-[#025979] focus:ring-[#025979] flex-1"
        />
        <Button
          data-testid="contributor-add-btn"
          onClick={handleAdd}
          disabled={busy}
          className="bg-[#025979] hover:bg-[#03738C] text-white text-sm font-medium rounded-sm transition-colors shadow-sm disabled:opacity-50 shrink-0"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          Add
        </Button>
      </div>
      {error && (
        <p data-testid="contributor-error" className="text-xs text-red-500 mt-2">
          {error}
        </p>
      )}

      {/* Send Invite — emails all active contributors */}
      <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
        <Button
          data-testid="contributor-send-invite-btn"
          onClick={handleInvite}
          disabled={inviting || activeCount === 0}
          className="bg-[#03738C] hover:bg-[#025979] text-white text-sm font-medium rounded-sm transition-colors shadow-sm disabled:opacity-50"
        >
          <Send className="w-4 h-4 mr-1.5" />
          {inviting ? "Sending…" : "Send Invite"}
        </Button>
        {activeCount === 0 && (
          <p className="text-[11px] text-[#9CA3AF] mt-2">
            Add at least one contributor to send invitations.
          </p>
        )}
        {inviteMsg && (
          <p data-testid="contributor-invite-result" className="text-xs text-[#065F46] mt-2">
            {inviteMsg}
          </p>
        )}
      </div>
    </div>
  );
};
